import { creatorbase, creatorbaseQueries, Song } from '@round/api';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import SongForm, { SongFormValues, validationSchema } from '../../components/SongForm/SongForm';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import styles from './UpdateSongModal.module.css';
import { formatSongFormValuesToApiData } from '../../components/SongForm/helpers';
import { showNotification } from 'helpers';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { useUpdateSongMutation, useInvalidateMusicSong } from '@round/creatorbase-helpers/api-hooks';

const updateUtils = creatorbaseQueries.song.utils.update;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    song: creatorbase.Song | null;
    musicSong: Song | null;
};

const getInitialValues = (song: creatorbase.Song | null, musicSong: Song | null): SongFormValues => ({
    title: song?.title ?? '',
    artist_name: song?.artist_name ?? '',
    status: song?.status ?? 'unreleased',
    spotify_url: musicSong?.spotify_song_url ?? '',
    tiktok_audios: song?.tiktok_audios ?? [],
    instagram_audios: song?.instagram_audios ?? [],
});

const UpdateSongModal = ({ isOpen, onClose, song, musicSong }: Props) => {
    const { update } = useUpdateSongMutation(song?.id);
    const invalidateMusicSong = useInvalidateMusicSong();

    const handleSubmit = async (values: SongFormValues, helpers: FormikHelpers<SongFormValues>) => {
        if (!song) {
            return;
        }

        try {
            const formattedValues = formatSongFormValuesToApiData(values, getInitialValues(song, musicSong));
            await update({ id: song.id, ...formattedValues });
            void invalidateMusicSong();
            showNotification('Song updated', 'info');
            formik.resetForm();
            onClose();
        } catch (e) {
            if (!(e instanceof Error) || !updateUtils.isExpectedErrorResponse(e) || e.response.status !== 400) {
                showNotification('Could not update song', 'error');
                return;
            }

            if (Array.isArray(e.response.data)) {
                showNotification(e.response.data.join(', '), 'error');
            } else {
                helpers.setErrors(mapApiErrorsToFormikErrors(e.response.data));
            }
        }
    };

    const formik = useFormik<SongFormValues>({
        initialValues: getInitialValues(song, musicSong),
        validationSchema,
        enableReinitialize: true,
        onSubmit: handleSubmit,
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
            <Modal.Header>
                <Modal.Title>Update song</Modal.Title>
            </Modal.Header>

            <FormikProvider value={formik}>
                <Modal.Body>
                    <SongForm
                        staticTiktokAudios={musicSong?.tiktok_audios}
                        staticInstagramAudios={musicSong?.instagram_audios}
                    />
                </Modal.Body>

                <Modal.Actions>
                    <Button
                        appearance="primary"
                        onClick={() => {
                            formik.handleSubmit();
                        }}
                        isLoading={formik.isSubmitting}
                        disabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    >
                        Update
                    </Button>
                    <Button appearance="ghost" onClick={handleClose}>
                        Cancel
                    </Button>
                </Modal.Actions>
            </FormikProvider>
        </Modal>
    );
};

export default UpdateSongModal;
